import React from 'react';
import { useIntl } from 'react-intl';

import Styles from '@fhs-legacy/frontend/src/styles';
import { Box, Header as UCLHeader } from '@fhs-legacy/universal-components';
import ActionButton from 'components/action-button';
import useEffectOnce from 'hooks/use-effect-once';
import { CustomEventNames, EventTypes, useCRMEventsContext } from 'state/crm-events';
import logger from 'utils/logger';

import IconError from './icon';
import theme from './theme';
import { useReload } from './use-reload';

const Container = Box.withConfig({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: Styles.color.grey.five,
});

const Inner = Box.withConfig({
  maxWidth: '380px',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  padding: '8',
});

// These styles are taken from the `cart-error` component,
// they should be extracted into a common component
const Header = UCLHeader.withConfig({
  textTransform: theme.textTransform,
  textAlign: 'center',
  marginY: '5',

  maxWidth: {
    base: '3/4',
    desktop: '500px',
  },
});

const Subheading = Box.withConfig({
  textAlign: 'center',
  fontSize: 'sm',
  marginBottom: '$4',
});

function ErrorBoundary({
  onRequestReload,
  errorReason,
}: {
  onRequestReload: () => void;
  errorReason: string;
}) {
  const { logRBIEvent } = useCRMEventsContext();
  const { formatMessage } = useIntl();
  const localeTitle = formatMessage({ id: 'errorBoundaryTitle' });
  const localeBody = formatMessage({ id: 'errorBoundaryBody' });
  const onReload = useReload(onRequestReload);

  useEffectOnce(() => {
    logger.error(new Error(errorReason));
    logRBIEvent({
      name: CustomEventNames.APP_ERROR,
      type: EventTypes.Other,
      attributes: {
        'error.message': errorReason,
      },
    });
  });

  return (
    <Container testID="error-boundary">
      <Inner>
        <IconError />
        <Header>{localeTitle}</Header>
        <Subheading>{localeBody}</Subheading>

        <ActionButton onPress={onReload} testID="error-boundary-reload-btn">
          {formatMessage({ id: 'reload' })}
        </ActionButton>
      </Inner>
    </Container>
  );
}

export default ErrorBoundary;
